<script>
	import { onMount, onDestroy } from 'svelte';
	import { currentUser } from '../store/store.js';
	import { navigate } from 'svelte-routing';
	import TopBar from '../components/TopBar.svelte';
	import { User } from '../utils/login.js'; // Assuming User class is exported from here
	import { writable } from 'svelte/store';
	import { Circle2 } from 'svelte-loading-spinners';
    import { Link } from 'svelte-routing';

	// Variables for user data
	let email = '';
	let firstName = '';
	let lastName = '';

	// Form state
	let loading = writable(false);
	let generalError = ''; // For initial load or delete errors
	let actionLoading = false; // Specific loading for Stripe actions
	let isSyncOnCooldown = false; // New state for sync button cooldown
	let cooldownTimer = null; // To store the timeout ID
	let syncButtonHiddenAfterSuccess = false; // New state variable

	// Input bindings (optional, for direct DOM manipulation if needed)
	let emailInput;
	let firstNameInput;
	let lastNameInput;

	// Base Stripe URLs
	const stripeBaseProPaymentLink = 'https://buy.stripe.com/8wM2962rq45n2HK4gg';
	const stripeBasePlusPaymentLink = 'https://buy.stripe.com/3cIdR92Wf557gYF1jKdIA01';
	const stripeBaseCancelLink = 'https://billing.stripe.com/p/login/bJe9AT68r413dMtfaAdIA00'; // Assuming this is the correct base for cancel/manage

	// Reactive Stripe links with prefilled email
	let stripeProPaymentLink = '';
	let stripePlusPaymentLink = '';
	let stripeCancelLink = '';

	$: {
		if ($currentUser) {
			email = $currentUser.username || '';
			firstName = $currentUser.first_name || '';
			lastName = $currentUser.last_name || '';

			if (email) { // Ensure email is populated before encoding
				const encodedEmail = encodeURIComponent(email);
				stripeProPaymentLink = `${stripeBaseProPaymentLink}?prefilled_email=${encodedEmail}`;
				stripePlusPaymentLink = `${stripeBasePlusPaymentLink}?prefilled_email=${encodedEmail}`;
				stripeCancelLink = `${stripeBaseCancelLink}?prefilled_email=${encodedEmail}`;
			} else {
				// Fallback if email is not yet available (e.g. $currentUser exists but username is missing)
				stripeProPaymentLink = stripeBaseProPaymentLink;
				stripePlusPaymentLink = stripeBasePlusPaymentLink;
				stripeCancelLink = stripeBaseCancelLink;
			}
		} else {
			// Clear local fields if no user
			email = '';
			firstName = '';
			lastName = '';

			// Fallback for Stripe links
			stripeProPaymentLink = stripeBaseProPaymentLink;
			stripePlusPaymentLink = stripeBasePlusPaymentLink;
			stripeCancelLink = stripeBaseCancelLink;
		}
	}

	onMount(async () => {
		syncButtonHiddenAfterSuccess = false; // Reset on component load/navigation
		loading.set(true);
		const isAuthenticated = await checkAuthStatus(); // This function will set $currentUser
													  // The reactive block above will update email, firstName, lastName.
		if (!isAuthenticated) {
			// If checkAuthStatus couldn't authenticate (neither localStorage nor backend)
			// and $currentUser is not valid, then navigate.
			if (!$currentUser || !$currentUser.accessToken) {
				navigate('/');
			}
		}
		loading.set(false); // Ensure loading is set to false after auth process
	});

    async function checkAuthStatus() {
		try {
			const storedUser = localStorage.getItem('currentUser');
			if (storedUser) {
				try {
					const userData = JSON.parse(storedUser);
					currentUser.set(new User(userData)); 
					// email, firstName, lastName will be set by the reactive block
					return true;
				} catch (e) {
					console.error("Error parsing stored user data:", e);
					localStorage.removeItem('currentUser'); // Clear invalid data
				}
			}
			
			// If currentUser is now set (e.g. from valid localStorage), return true
			// The reactive block will handle populating email, firstName, lastName.
			if ($currentUser && $currentUser.accessToken) {
				return true;
			}
			
			// Check if we have a token in cookies before making the request
			// This prevents the 401 error when no authentication exists
			const hasAuthCookie = document.cookie.split(';').some(cookie => 
				cookie.trim().startsWith('auth=') || cookie.trim().startsWith('session=')
			);
			
			if (!hasAuthCookie) {
				// No auth cookie found, skip the server request
				currentUser.set(null); // Ensure user is cleared if no cookie
				localStorage.removeItem('currentUser');
				return false;
			}
			
			// Only try server authentication if we might have a valid session
			try {
				const response = await fetch(`https://boanalys-backend-324523837899.europe-north1.run.app/users/me`, {
					credentials: 'include',
					headers: {
						'Accept': 'application/json'
					}
				});
				
				if (response.ok) {
					const serverData = await response.json();
					const currentTokenInStore = $currentUser?.accessToken || JSON.parse(localStorage.getItem('currentUser'))?.accessToken; 
                    // Fallback to localStorage token if $currentUser isn't set yet but we had a storedUser attempt

					// Create user object with all fields from serverData and existing token
					const userObj = new User({
						accessToken: currentTokenInStore, // Use the token that authenticated this request
						username: serverData.username,
						type: serverData.type,
						numberOfAnalysis: serverData.number_of_analysis,
						first_name: serverData.first_name,
						last_name: serverData.last_name,
						stripe_customer_id: serverData.stripe_customer_id,
						subscription_current_period_end: serverData.subscription_current_period_end,
						subscription_status: serverData.subscription_status, // Crucial for delete check
						subscription_tier: serverData.subscription_tier
						// Add any other fields returned by /users/me and expected by User constructor
					});
					
					// Store in localStorage for future use
					localStorage.setItem('currentUser', JSON.stringify(userObj));
					
					// Set user in store
					currentUser.set(userObj);
					
					// email, firstName, lastName will be set by the reactive block
					
					return true;
				} else if (response.status === 401) {
					// User is not authenticated
					currentUser.set(null);
					localStorage.removeItem('currentUser'); // Clear any stale data
					return false;
				} else {
					// Other error
					console.error(`Authentication check failed: ${response.status} ${response.statusText}`);
					return false;
				}
			} catch (error) {
				console.error("Error checking authentication status:", error);
				return false;
			}
		} catch (error) {
			console.error("Error checking authentication status:", error);
			return false;
		}
	}

	async function syncSubscriptionStatus() {
		if (!$currentUser?.accessToken) {
			generalError = 'Autentisering krävs. Logga in igen.';
			return;
		}

		if (isSyncOnCooldown) {
			generalError = 'Vänta en stund innan du försöker uppdatera igen.';
			return;
		}

		actionLoading = true;
		isSyncOnCooldown = true; // Start cooldown immediately
		// generalError is not cleared here, it will be set based on this function's outcome.
		// syncButtonHiddenAfterSuccess is not reset here; it's set based on outcome.

		try {
			const response = await fetch(`https://boanalys-backend-324523837899.europe-north1.run.app/users/me/sync-subscription`, {
				method: 'GET',
				credentials: 'include', // Rely on this to send the authentication cookie
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (response.ok) { // Handle 200 OK responses
				const responseText = await response.text(); // Get response as text first
				try {
					const parsedData = JSON.parse(responseText); // Try to parse as JSON

					// Case 1: Parsed data is a JSON object (expected user data)
					if (typeof parsedData === 'object' && parsedData !== null && !Array.isArray(parsedData)) {
						const serverData = parsedData;
						const currentTokenInStore = $currentUser?.accessToken;

						if (!currentTokenInStore) {
							generalError = "Kunde inte uppdatera användarinformationen lokalt, token saknas.";
							await checkAuthStatus(); // Attempt to recover/clear session
							actionLoading = false; // Ensure loading state is reset
							return; // Exit as we can't form a valid User object
						}
						
						const userObj = new User({
							accessToken: currentTokenInStore,
							username: serverData.username,
							type: serverData.type,
							numberOfAnalysis: serverData.number_of_analysis,
							first_name: serverData.first_name,
							last_name: serverData.last_name,
							stripe_customer_id: serverData.stripe_customer_id,
							subscription_current_period_end: serverData.subscription_current_period_end,
							subscription_status: serverData.subscription_status,
							subscription_tier: serverData.subscription_tier
						});
						
						localStorage.setItem('currentUser', JSON.stringify(userObj));
						currentUser.set(userObj);
						
						// email, firstName, lastName will be set by the reactive block
						generalError = ''; // Clear any previous errors on full success
					} 
					// Case 2: Parsed data is a string (JSON-encoded string message from server)
					else if (typeof parsedData === 'string') {
						if (parsedData.trim() !== '') {
							generalError = parsedData;
						} else {
							generalError = 'Synkroniseringen slutfördes med ett tomt meddelande.'; 
						}
						await checkAuthStatus(); 
					} 
					// Case 3: Parsed data is some other valid JSON type (array, number, boolean, null)
					else {
						generalError = 'Mottog oväntat dataformat från servern efter lyckad synkronisering.';
						console.warn("Sync subscription: Unexpected JSON type received with 200 OK:", parsedData);
						await checkAuthStatus(); // Refresh state
					}
				} catch (e) {
					// Case 4: JSON.parse(responseText) failed. 
					if (responseText && responseText.trim() !== '') {
						generalError = responseText; // Display the plain string message
					} else {
						generalError = 'Mottog ett tomt eller ogiltigt textmeddelande från servern.';
					}
					console.warn("Sync subscription: Response was plain text or invalid JSON with 200 OK:", responseText);
					await checkAuthStatus(); 
				}

				// After currentUser is potentially updated by the sync response or checkAuthStatus:
				if ($currentUser && ($currentUser.subscription_status === 'active' || $currentUser.subscription_status === 'trialing')) {
					// generalError is NOT cleared here anymore, it retains any message from the sync.
					syncButtonHiddenAfterSuccess = true; 
				} else {
					syncButtonHiddenAfterSuccess = false; 
				}
			} else {
				// This block handles non-200 OK responses (errors from the server)
				// This uses the logic from your previously accepted change.
				let errorMessage = 'Ett fel uppstod vid synkronisering av prenumerationsstatus.'; // Default fallback
				try {
					const errorResponseContent = await response.json();
					if (typeof errorResponseContent === 'string' && errorResponseContent.trim() !== '') {
						errorMessage = errorResponseContent; // Use the string directly if it's not empty
					} else if (errorResponseContent && typeof errorResponseContent.detail === 'string') {
						errorMessage = errorResponseContent.detail; // Use the detail field from JSON
					}
				} catch (e) {
					console.error("Error parsing sync subscription error response (non-OK):", e);
				}
				generalError = errorMessage;
				// Vid fel från synkroniseringen, försök ändå att uppdatera från /users/me
				await checkAuthStatus(); 
				syncButtonHiddenAfterSuccess = false; // Ensure button is shown on error
			}
		} catch (error) {
			console.error("Error syncing subscription status:", error);
			generalError = 'Ett nätverksfel uppstod vid synkronisering. Försök igen.';
			// Vid nätverksfel, försök också att uppdatera från /users/me
			await checkAuthStatus();
			syncButtonHiddenAfterSuccess = false; // Ensure button is shown on error
		} finally {
			actionLoading = false;
			// Start the timer to end the cooldown
			cooldownTimer = setTimeout(() => {
				isSyncOnCooldown = false;
			}, 30000); // 30 seconds (30000 milliseconds)
		}
	}

	// Helper function to format date
	function formatDate(timestamp) {
		if (!timestamp) return 'Information saknas';
		let date;
		// Assuming timestamp from backend is in seconds or an ISO string
		if (typeof timestamp === 'number' && timestamp > 10000000000) { // Likely ms
			date = new Date(timestamp);
		} else if (typeof timestamp === 'number') { // Likely seconds
			date = new Date(timestamp * 1000);
		} else { // ISO string
			date = new Date(timestamp);
		}
		if (isNaN(date.getTime())) return 'Ogiltigt datum';
		return date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });
	}

	async function handleDeleteAccount() {
		const user = $currentUser;

		if (!user || !user.accessToken) {
			generalError = 'Användarinformation saknas. Prova att ladda om sidan.';
			// navigate('/login'); // Or simply prevent action
			return;
		}

		// 1. Check subscription status
		// Ensure $currentUser.subscription_status is reliably populated by checkAuthStatus.
		if (user.subscription_status === 'active' || user.subscription_status === 'trialing') { 
			generalError = 'Du har en aktiv prenumeration. Du måste först avsluta din prenumeration innan du kan ta bort ditt konto.';
			return; 
		}

		// 2. Enhanced Confirmation Prompt
		const confirmationPhrase = "RADERA MITT KONTO"; // Case-sensitive
		const promptMessage = `Är du helt säker på att du vill ta bort ditt konto? All din data kommer att raderas permanent och detta kan inte ångras.\n\nFör att bekräfta, skriv exakt "${confirmationPhrase}" i fältet nedan:`;
		
		const userInput = prompt(promptMessage);

		if (userInput === null) {
			// User clicked "Cancel" on the prompt
			alert('Borttagningen avbröts.');
			return; 
		}

		if (userInput !== confirmationPhrase) { 
			alert('Bekräftelsetexten matchade inte. Borttagningen avbröts.');
			return;
		}

		// If all checks pass, proceed with deletion
		loading.set(true);
		generalError = ''; // Clear any previous general errors

		try {

			const response = await fetch('https://boanalys-backend-324523837899.europe-north1.run.app/users/me', {
				method: 'DELETE',
				credentials: 'include', 
				headers: {
					'Authorization': `Bearer ${user.accessToken}`
				}
			});

			if (response.ok) {
				currentUser.set(null);
				localStorage.removeItem('currentUser');
				alert('Ditt konto har tagits bort.');
				navigate('/');
			} else {
				const errorData = await response.json().catch(() => ({ detail: 'Kunde inte tolka felmeddelande från servern.' }));
				generalError = errorData.detail || 'Kunde inte ta bort kontot. Försök igen senare.';
				console.error(`Account deletion failed: ${response.status} ${response.statusText}`, errorData);
			}
		} catch (error) {
			generalError = 'Ett nätverksfel uppstod vid borttagning av kontot. Kontrollera din anslutning och försök igen.';
			console.error('Error deleting account:', error);
		} finally {
			loading.set(false);
		}
	}

	onDestroy(() => {
		if (cooldownTimer) {
			clearTimeout(cooldownTimer); // Clear timeout on component destroy
		}
	});
</script>

<div>
	<TopBar />
	<div class="page-container"></div>

	{#if $loading && !email && !firstName && !lastName}
		<!-- Centered loading spinner for the whole page -->
		<div style="display: flex; justify-content: center; align-items: center; min-height: 50vh;">
			<Circle2 size="60" color="#FF3E00" unit="px" duration="1s" />
		</div>
	{:else if generalError && !$currentUser && !actionLoading } 
        <!-- General error if user data couldn't be loaded -->
		<div class="content-box" style="margin-top: 10vh; max-width: 800px; text-align: center;">
            <p class="error-message">{generalError}</p>
        </div>
	{:else if $currentUser?.accessToken}
		<!-- Subscription Box -->
		{#if $currentUser.subscription_status !== 'active' && $currentUser.subscription_status !== 'trialing'}
		<div class="content-box subscription-details-box">
			<div class="subscription-section">
				<div class="subscription-upgrade">
					<h4>Välj din plan</h4>						
					<!-- Errors from Stripe actions will be shown in the profile-details-box below if generalError is set and actionLoading is false -->

					<div class="plans-container">
						<div class="plan-card">
							<h5>Plus</h5>
							<div class="price-section">
								<span class="price-amount">49</span>
								<span class="price-term">kr / månad</span>
							</div>
							<a href={stripePlusPaymentLink} class="action-button" target="_blank" rel="noopener noreferrer">
								Välj Plus
							</a>
							<ul class="features-list">
								<li>BoBot 10 frågor per månad</li>
								<li>Tillgång till ÅR</li>
								<li>Tillgång till Stadgar</li>
								<li>Analyser - 3 per månad</li>
								<li>Betyg - standard</li>
								<li>Kartvy</li>
								<li>Många nyckeltal</li>
							</ul>
						</div>
						<div class="plan-card">
							<h5>Pro</h5>
							<div class="price-section">
								<span class="price-amount">99</span>
								<span class="price-term">kr / månad</span>
							</div>
							<a href={stripeProPaymentLink} class="action-button" target="_blank" rel="noopener noreferrer">
								Välj Pro
							</a>
							<ul class="features-list">
								<li>BoBot 100 frågor per månad</li>
								<li>Tillgång till Energideklaration</li>
								<li>Analyser - 10 per månad</li>
								<li>Betyg - detaljerad</li>
								<li>Trendgrafer</li>
								<!-- <li>Jämföra föreningar</li> -->
								<li>Områdesanalyser</li>
								<li>Kontaktinfo - 10 per månad</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
			<p style="font-size: 0.8em; color: #6c757d; margin-top: 10px; margin-bottom: 0px;">
				* Om du har en aktiv prenumeration kommer du inte kunna ändra plan.
				<br>
				** Du måste ange samma e-postadress som du använde för att skapa ditt konto.
			</p>
		</div>
		{/if}

		<!-- Profile Details Box -->
		<div class="content-box profile-details-box">
			<div class="header-card">
				<h5>Konto</h5>
			</div>
            
			{#if generalError && !actionLoading} <!-- Shows errors from Stripe (after load) or Delete Account -->
			    <p class="error-message" style="margin-top: 10px; text-align: center;">{generalError}</p>
			{/if}

			<div class="profile-details-grid">
				<div class="subscription-summary-section">
					<h4>Personlig information</h4>
					<div class="info-item">
						<span class="info-label">E-postadress</span>
						<span class="info-value">{email}</span>
					</div>
					{#if $currentUser.first_name && $currentUser.first_name !== '' && $currentUser.last_name && $currentUser.last_name !== ''}
					<div class="info-item">
						<span class="info-label">Förnamn</span>
						<span class="info-value">{firstName}</span>
					</div>
					<div class="info-item">
						<span class="info-label">Efternamn</span>
						<span class="info-value">{lastName}</span>
					</div>
					{:else}
					<div class="info-item">
						<span class="info-label">Namn</span>
						<span class="info-value">{firstName}</span>
					</div>
					{/if}
					{#if $currentUser.subscription_status === 'active' || $currentUser.subscription_status === 'trialing'}
						<a href={stripeCancelLink} class="action-button" target="_blank" rel="noopener noreferrer">
							Hantera prenumeration
						</a>
					{/if}
				</div>

				{#if $currentUser.subscription_status === 'active' || $currentUser.subscription_status === 'trialing'}
				<div class="subscription-summary-section">
					<h4>Ditt abonnemang</h4>
					<div class="info-item">
						<span class="info-label">Plan</span>
						<span class="info-value">{$currentUser.subscription_tier || 'Okänd'}</span>
					</div>
					{#if $currentUser.subscription_current_period_end}
					<div class="info-item">
						<span class="info-label">Förnyas</span>
						<span class="info-value">{formatDate($currentUser.subscription_current_period_end)}</span>
					</div>
					{/if}
					<div class="info-item">
						<span class="info-label">Status</span>
						<span class="info-value status-{$currentUser.subscription_status}">
							{#if $currentUser.subscription_status === 'active'}Aktiv
							{:else if $currentUser.subscription_status === 'trialing'}Provperiod
							{:else}Okänd{/if}
						</span>
					</div>
					{#if !syncButtonHiddenAfterSuccess}
					<button class="action-button sync-button" on:click={syncSubscriptionStatus} disabled={actionLoading || isSyncOnCooldown}>
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-refresh-cw"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
						{actionLoading ? 'Laddar...' : (isSyncOnCooldown ? 'Vänta...' : 'Uppdatera Status')}
					</button>
					{/if}
				</div>
				{:else}
				<div class="subscription-summary-section">
					<h4>Ditt abonnemang</h4>
					<div class="info-item">
						<span class="info-label">Status</span>
						<span class="info-value status-{$currentUser.subscription_status}">
							{#if $currentUser.subscription_status === 'inactive'}Inaktiv
							{:else if $currentUser.subscription_status === 'canceled'}Avslutad
							{:else if $currentUser.subscription_status === 'incomplete'}Ofullständig
							{:else if $currentUser.subscription_status === 'incomplete_expired'}Ofullständig (utgånget försök)
							{:else if $currentUser.subscription_status === 'past_due'}Förfallen
							{:else if $currentUser.subscription_status === 'unpaid'}Obetald
							{:else}Okänd{/if}
						</span>
					</div>
					<button class="action-button sync-button" on:click={syncSubscriptionStatus} disabled={actionLoading || isSyncOnCooldown}>
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-refresh-cw"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
						{actionLoading ? 'Laddar...' : (isSyncOnCooldown ? 'Vänta...' : 'Uppdatera Status')}
					</button>
				</div>
				{/if}
			</div>
			
			<!--
			<div class="section-divider"></div>
			{#if $currentUser.subscription_status === 'active' || $currentUser.subscription_status === 'trialing'}
			<a href={stripeCancelLink} class="action-button" target="_blank" rel="noopener noreferrer">
				Hantera prenumeration
			</a>
			{/if}
			<div style="margin-top: 20px; text-align: left;">
				<span on:click={handleDeleteAccount} on:keydown={ (e) => { if (e.key === 'Enter' || e.key === ' ') handleDeleteAccount(); }} tabindex="0" role="button" class="delete-account-link" style="color: #dc3545; cursor: pointer; font-size: 0.9em; text-decoration: underline;" disabled={$loading || actionLoading}>
					{#if $loading}Laddar...{:else}Ta bort konto{/if}
				</span>
			</div>
			-->
		</div>
	{:else}
		<p style="text-align: center; margin-top: 20px;">Omdirigerar...</p>
	{/if}

	<div class="black-div" style="margin-top: 50px;"></div>

	<footer>
        <p>©Boanalys. All rights reserved.</p>
        <p><Link to="/about#kontakt-section">Kontakta oss</Link></p>
    </footer>
</div>

<style>
	.page-container {
		margin-top: 100px;
	}		
	.content-box {
		background-color: #fff;
		border-radius: 12px; /* From Rating.svelte */
		box-shadow: 0 2px 4px 0 rgba(4, 4, 52, .06), 0 0 2px 0 rgba(91, 95, 107, .24); /* From Rating.svelte */
		padding: 20px; /* Adjusted padding, Rating.svelte has 20px 20px 16px 20px */
		margin-left: auto; /* Ensure no !important here */
		margin-right: auto; /* Ensure no !important here */
		max-width: 660px; /* Or 660px if you preferred the original, but 800px matches the media query breakpoint well */
		transition: box-shadow 0.3s ease; /* From Rating.svelte */
	}

	@media (max-width: 800px) {
		.content-box {
			margin-left: 15px;
			margin-right: 15px;
		}
	}

	.content-box:hover {
		box-shadow: 0 8px 12px 0 rgba(4, 4, 52, 0.1), 0 0 4px 0 rgba(91, 95, 107, 0.3); /* From Rating.svelte */
	}

	.subscription-details-box {
		margin-bottom: 30px; 
	}

	.subscription-details-box .subscription-section {
		margin-top: 0;
		padding-top: 0;
		border-top: none;
	}
	.subscription-details-box h4 { 
		text-align: center; /* Default alignment for titles like "Ditt abonnemang" */
		margin-bottom: 20px; 
		margin-top: 0; 
		font-weight: 600;
		font-size: 1.4em; 
		color: #333;
	}

	/* Specifically left-align "Välj din plan" title and style it */
	.subscription-upgrade > h4 {
		text-align: left;
		font-family: 'MuseoModerna', sans-serif;
		font-size: 1.9em; /* Prominent size */
		font-weight: 600; 
		color: #1c1c1e; /* Dark, modern color */
		margin-bottom: 10px; /* Reduced margin to bring paragraph closer */
		margin-top: 0; 
	}

	/* Style for the "current subscription" info area, similar to metric-card */
	.current-subscription.metric-card-style {
		background-color: #f8f9fa; /* From Rating.svelte .metric-card */
		border-radius: 8px; /* From Rating.svelte .metric-card */
		padding: 20px; /* Adjusted padding */
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); /* From Rating.svelte .metric-card */
		margin-bottom: 30px; /* Space if plans are shown below (though not in active state) */
		font-weight: 600;
	}


	.profile-details-box {
		margin-bottom: 30px; 
	}

	.profile-details-box form h4 { /* This style can likely be removed or adapted if form structure changes significantly */
		margin-bottom: 5px;
		margin-top: 15px;
		font-weight: 500;
		text-align: left;
		font-size: 0.95em; /* Slightly adjusted */
		color: #555;
	}


	/* New styles for Personuppgifter section */
	.profile-details-grid {
		display: grid;
		grid-template-columns: 1fr; /* Single column by default */
		gap: 20px; /* Gap between sections when stacked */
		margin-top: 20px;
	}

	@media (min-width: 768px) { /* Two columns for wider screens */
		.profile-details-grid {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); /* Personal info, Subscription summary */
			gap: 30px; /* Gap between columns */
		}
	}

	.subscription-summary-section h4 {
		font-size: 1.1em;
		font-weight: 600;
		color: #333;
		margin-bottom: 15px;
		text-align: left; /* Title for subscription summary */
	}

	.info-item {
		margin-bottom: 18px;
	}

	.info-label {
		display: block;
		font-size: 0.8em; /* Smaller label */
		color: #6c757d; /* Greyish color for label */
		margin-bottom: 4px;
		text-transform: uppercase; /* Optional: Uppercase labels */
		letter-spacing: 0.5px; /* Optional: Slight letter spacing */
	}

	.info-value {
		display: block;
		font-size: 1em;
		color: #333;
		font-weight: 500;
		padding: 6px 0; /* Reduced padding slightly */
	}
	
	/* Styling for status within the summary, reusing existing status classes */
	.subscription-summary-section .info-value.status-active {
		color: #28a745;
		font-weight: bold;
	}
	.subscription-summary-section .info-value.status-inactive {
		color: #dc3545;
		font-weight: bold;
	}
	.subscription-summary-section .info-value.status-unknown {
		color: #6c757d;
		font-weight: bold;
	}

	.subscription-summary-section .action-button {
		margin-top: 15px;
	}

	.plans-container {
		display: flex;
		justify-content: center; /* Changed from space-around to center */
		gap: 20px;
		margin-top: 20px;
		flex-wrap: wrap; /* Allow wrapping on smaller screens */
	}

	.plan-card {
		background-color: #f8f9fa; /* From Rating.svelte .metric-card */
		border: none; /* Removed border, rely on shadow and background */
		border-radius: 8px; /* From Rating.svelte .metric-card */
		padding: 20px; /* Adjusted padding */
		min-width: 280px; /* Adjusted min-width */
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); /* From Rating.svelte .metric-card */
		display: flex;
		flex-direction: column;
		text-align: left; 
	}

	.plan-card:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
	}

	.plan-card h5 { 
		font-size: 1.15em; /* Adjusted size */
		font-weight: 600; 
		color: #333; 
		margin-bottom: 8px; 
	}

	.price-section {
		margin-bottom: 20px; /* Space before button */
		display: flex;
		align-items: baseline; /* Align amount and term nicely */
		font-family: 'MuseoModerna';
	}

	.price-amount {
		font-size: 2.8em; /* Larger font for price amount */
		font-weight: bold;
		color: #111; /* Very dark grey / black */
		line-height: 1;
	}

	.price-term {
		font-size: 1em;
		color: #555; /* Slightly lighter text for "/månad" */
		margin-left: 8px;
	}
	
	.features-list {
		list-style: none;
		padding-left: 0;
		margin-top: 20px; /* Space after button */
		margin-bottom: 0; /* No extra margin at the bottom of list */
		text-align: left;
		width: 100%;
		flex-grow: 1; /* Allow list to take available space */
	}

	.features-list li {
		margin-bottom: 10px; /* Increased space between features */
		font-size: 0.9em; /* Slightly smaller feature text */
		padding-left: 25px; 
		position: relative;
		color: #333;
	}
	.features-list li::before {
		content: '✓'; 
		color: var(--hover); /* Blue checkmark, or your primary color */
		position: absolute;
		left: 0;
		font-weight: bold;
		font-size: 1.1em; /* Slightly larger checkmark */
	}


	.action-button:hover:not([disabled]) {
		background-color: #495057;
		transform: translateY(-2px);
		box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
	}
	.action-button:active:not([disabled]) {
		transform: translateY(0px);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}
	.action-button:disabled {
		background-color: #6c757d; /* Adjusted disabled background */
		color: #adb5bd; /* Adjusted disabled text color */
		cursor: not-allowed;
		transform: none;
		box-shadow: 0 1px 1px rgba(0,0,0,0.1); /* Flatter shadow for disabled */
	}

	.plan-card .action-button {
		padding: 12px 20px; /* Retain slightly larger padding for these buttons */
		margin-top: 0; /* Positioned directly after price section */
		margin-bottom: 20px; /* Space before features list */
	}

	.error-message { /* Ensure error messages are styled */
		color: #dc3545; /* Red color for errors */
		font-size: 0.9em;
	}

	.delete-account-link:hover {
		color: #a71d2a; /* Darker red on hover */
		text-decoration: underline;
	}
	.delete-account-link[disabled] {
		color: #6c757d;
		cursor: not-allowed;
		text-decoration: none;
	}

	/* Divider Style */
	.section-divider {
		height: 1px;
		background-color: #e9ecef; /* Light grey, similar to other subtle borders */
		margin-top: 25px; /* Space above the divider */
		margin-bottom: 25px; /* Space below the divider */
	}

	.sync-button {
		display: inline-flex; /* To align icon and text */
		align-items: center;
		justify-content: center;
		gap: 8px; /* Space between icon and text */
		width: auto !important; /* Override the 100% width from the general .action-button if needed, or make it specific */
		padding-left: 15px; /* Adjust padding if width is auto */
		padding-right: 15px;
	}
</style>
